import { useState, useEffect } from 'react'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useToast } from '@/hooks/use-toast'
import { Search, AlertCircle, Wifi, WifiOff } from 'lucide-react'
import { Badge } from '@/components/ui/badge'

import { useTaxonomyRun, setDevOverride, DevOverride, TaxonomyRun } from '@/hooks/useTaxonomyRun'
import { useKnowledgeBaseWebSocket, ConnectionState } from '@/hooks/useKnowledgeBaseWebSocket'
import { EmptyState }     from './EmptyState'
import { RunningState }   from './RunningState'
import { CompletedState } from './CompletedState'
import { ErrorState }     from './ErrorState'

interface GenClairTabProps {
  kbId:        string
  parsedCount: number
  totalCount:  number
}

export function GenClairTab({ kbId, parsedCount, totalCount }: GenClairTabProps) {
  const { latestRun, fullResult, graphData, createRun, deleteRun } = useTaxonomyRun(kbId)
  
  // Use the unified KB WebSocket for all real-time updates
  const { 
    connectionState: wsConnectionState, 
    taxonomyStatus: wsTaxonomyStatus,
    clearTaxonomyStatus
  } = useKnowledgeBaseWebSocket(kbId)
  
  const { toast } = useToast()
  const isDev = import.meta.env.DEV

  // ── Dev-mode override state ──
  const [devMode, setDevMode] = useState<DevOverride>(null)
  
  // ── Manual state tracking for discovery job ──
  // This should start as false unless we just clicked the button
  const [forceRunningState, setForceRunningState] = useState(false)
  
  // ── Track our own progress to show before API responds ──
  const [optimisticProgress, setOptimisticProgress] = useState(0)

  // ── Derive effective state ──
  const run = latestRun.data
  
  useEffect(() => {
    setDevOverride(devMode)
    // Force a refetch so the hook picks up the new override
    latestRun.refetch()
  }, [devMode])
  
  // Reset forceRunningState when we get run data or if status changes
  // Use WebSocket data if available, otherwise fall back to API data
  const effectiveRun: TaxonomyRun | undefined = wsTaxonomyStatus && wsConnectionState === ConnectionState.CONNECTED 
    ? {
        run_id: wsTaxonomyStatus.run_id,
        status: wsTaxonomyStatus.status as TaxonomyRun['status'],
        progress: wsTaxonomyStatus.progress,
        total_chunks_processed: wsTaxonomyStatus.total_chunks_processed,
        intents_discovered: wsTaxonomyStatus.intents_discovered,
        error_log: wsTaxonomyStatus.error_log || '',
        created_at: wsTaxonomyStatus.created_at,
        completed_at: wsTaxonomyStatus.completed_at,
        // Include step info from WebSocket
        step: wsTaxonomyStatus.step,
        chunks_in_step: wsTaxonomyStatus.chunks_in_step,
      }
    : run ?? undefined;
  
  useEffect(() => {
    if (effectiveRun) {
      console.log('Run data detected, state:', effectiveRun.status, 'progress:', effectiveRun.progress)
      
      // Only turn off force flag if status is a terminal state
      if (effectiveRun.status === 'completed' || effectiveRun.status === 'failed') {
        setForceRunningState(false)
        setOptimisticProgress(0)
      }
    }
  }, [effectiveRun?.status, effectiveRun?.progress])
  
  // Increment optimistic progress if forceRunningState is true and no real progress yet
  useEffect(() => {
    let progressTimer: number | null = null;
    
    if (forceRunningState && (!run || run.progress < 5)) {
      // Start with 3% and increment by 1% every 2 seconds until we get real data
      setOptimisticProgress(3);
      
      progressTimer = window.setInterval(() => {
        setOptimisticProgress(prev => {
          // Don't go above 20% with optimistic updates
          return prev < 20 ? prev + 1 : prev;
        });
      }, 2000);
    }
    
    return () => {
      if (progressTimer) window.clearInterval(progressTimer);
    };
  }, [forceRunningState, run?.progress]);
  const isStartingPipeline = createRun.isPending

  // Determine which UI state to show
  // Priority: if devMode is set, it wins. Otherwise derive from WebSocket/API data.
  let uiState: 'empty' | 'running' | 'completed' | 'error'
  
  if (isDev && devMode) {
    // Dev mode - use the selected state
    uiState = devMode === 'error' ? 'error' : devMode
  } else if (isStartingPipeline || forceRunningState) {
    // If we're in the process of starting a pipeline or we've manually set running state
    // show running state with optimistic UI
    uiState = 'running'
  } else if (!effectiveRun) {
    // No run data yet
    uiState = 'empty'
  } else if (effectiveRun.status === 'completed') {
    uiState = 'completed'
  } else if (effectiveRun.status === 'failed') {
    uiState = 'error'
  } else if (effectiveRun.status === 'pending' || effectiveRun.status === 'running') {
    // Explicit check for pending or running status
    uiState = 'running'
  } else {
    // Fallback
    uiState = 'empty'
  }

  // ── Action handlers ──
  const handleDiscover = async () => {
    try {
      // Immediately set force running state to true to ensure UI updates
      setForceRunningState(true)
      
      // If there's an existing failed run, delete it first
      if (effectiveRun?.status === 'failed' && effectiveRun.run_id) {
        console.log('Deleting failed run before retry:', effectiveRun.run_id)
        await deleteRun.mutateAsync(effectiveRun.run_id)
        clearTaxonomyStatus()
      }
      
      // Execute the mutation to create the run
      const newRun = await createRun.mutateAsync()
      
      // Force an immediate refetch of the latest run to show running state
      await latestRun.refetch()
      
      // Log to console for debugging
      console.log('Created new taxonomy run:', newRun)
    } catch (err: any) {
      // If error occurs, reset the force running state
      setForceRunningState(false)
      toast({
        title: 'Failed to start pipeline',
        description: err?.response?.data?.error || 'Something went wrong.',
        variant: 'destructive',
      })
    }
  }
  
  const handleDelete = async (runId: string) => {
    try {
      await deleteRun.mutateAsync(runId)
      // Clear WebSocket state so stale data doesn't show
      clearTaxonomyStatus()
      toast({
        title: 'Taxonomy deleted',
        description: 'The taxonomy data has been successfully deleted.',
      })
    } catch (err: any) {
      toast({
        title: 'Failed to delete taxonomy',
        description: err?.response?.data?.error || 'Something went wrong.',
        variant: 'destructive',
      })
    }
  }

  // ── Render ───────────────────────────────────────────────────────
  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Search className="w-4 h-4 text-blue-600" />
            GenClair — Taxonomy Discovery
            
            {/* WebSocket connection status indicator */}
            {wsConnectionState && (
              <Badge 
                variant={wsConnectionState === ConnectionState.CONNECTED ? 'outline' : 'secondary'}
                className={`ml-2 ${wsConnectionState === ConnectionState.CONNECTED ? 'bg-green-50 text-green-700 border-green-200' : 'bg-amber-50 text-amber-700'}`}
              >
                {wsConnectionState === ConnectionState.CONNECTED ? (
                  <><Wifi className="w-3 h-3 mr-1" /> Realtime</>
                ) : (
                  <><WifiOff className="w-3 h-3 mr-1" /> {wsConnectionState}</>
                )}
              </Badge>
            )}
          </CardTitle>

          {/* Dev-mode state switcher — invisible in production */}
          {isDev && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400">Preview:</span>
              <Select
                value={devMode ?? 'live'}
                onValueChange={(v) => setDevMode(v === 'live' ? null : v as DevOverride)}
              >
                <SelectTrigger className="w-[120px] h-7 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="live">Live</SelectItem>
                  <SelectItem value="empty">Empty</SelectItem>
                  <SelectItem value="running">Running</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="error">Error</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </CardHeader>

      {parsedCount > 0 && parsedCount < totalCount && (
        <div className="mx-4 mb-2 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
          <AlertCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
          <p className="text-xs text-amber-700">
            Based on <span className="font-semibold">{parsedCount}</span> of{' '}
            <span className="font-semibold">{totalCount}</span> parsed documents.
            Parse remaining documents for a more complete taxonomy.
          </p>
        </div>
      )}

      <CardContent>
        {uiState === 'empty' && (
          <EmptyState onDiscover={handleDiscover} isLoading={createRun.isPending} />
        )}
        
        {uiState === 'running' && (
          <>
            <RunningState 
              run={effectiveRun ? effectiveRun : {
                // Optimistic UI data while waiting for the first API response
                run_id: 'pending',
                status: 'running',
                progress: optimisticProgress,
                total_chunks_processed: 0,
                intents_discovered: 0,
                error_log: '',
                created_at: new Date().toISOString(),
                completed_at: null
              }} 
            />
            {/* Show current pipeline step when available from WebSocket */}
            {effectiveRun?.step && wsConnectionState === ConnectionState.CONNECTED && (
              <div className="mt-2 text-center">
                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                  Current step: {effectiveRun.step}
                  {effectiveRun.chunks_in_step !== undefined && effectiveRun.chunks_in_step > 0 && (
                    <span className="ml-1">({effectiveRun.chunks_in_step} chunks)</span>
                  )}
                </Badge>
              </div>
            )}
          </>
        )}
        {uiState === 'completed' && effectiveRun && (
          <CompletedState
            run={effectiveRun}
            intents={fullResult.data?.intents ?? []}
            graphData={graphData.data ?? { nodes: [], edges: [] }}
            kbId={kbId}
            onReRun={handleDiscover}
            isReRunning={createRun.isPending}
            onDelete={handleDelete}
            isDeleting={deleteRun.isPending}
          />
        )}
        {uiState === 'error' && effectiveRun && <ErrorState run={effectiveRun} onRetry={handleDiscover} isRetrying={createRun.isPending} />}
      </CardContent>
    </Card>
  )
}
